"use client"

import Image from "next/image"
import { Menu, Phone } from "lucide-react"
import { Env } from "@/env.mjs"
import { FastLink } from "./ui/fast-link"
import { cleanPhoneNumber, cn, copyToClipboard } from "@/lib/utils"
import { toast } from "sonner"
import { Button } from "./ui/button"
import { StorefrontRoutesMap } from "@repo/api-shared"
import { CallRequestDialog } from "./custom/dialogs"
import { useState, useEffect } from "react"
import { usePathname } from "next/navigation"
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet"

const NavigationLinks = [
    {
        href: StorefrontRoutesMap.CATALOG,
        label: "Каталог",
    },
    {
        href: StorefrontRoutesMap.REVIEWS,
        label: "Отзывы",
    },
    {
        href: StorefrontRoutesMap.CONTACTS,
        label: "Контакты",
    },
]

export default function Header() {
    const [dialogOpen, setDialogOpen] = useState(false)

    const pathname = usePathname()

    const handleCopyNumber = () => {
        copyToClipboard(cleanPhoneNumber(Env.NEXT_PUBLIC_PHONE_NUMBER))
        toast.success("Номер телефона скопирован")
    }

    return (
        <>
            <header
                className={cn(
                    "absolute left-0 top-0 z-50 h-[4rem] w-full border-b border-primary",
                    pathname === StorefrontRoutesMap.HOME &&
                        "fixed bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
                )}
            >
                <div className="container mx-auto px-4 py-3">
                    <div className="flex items-center justify-between lg:grid lg:grid-cols-[1fr_auto_1fr]">
                        <FastLink
                            prefetch={true}
                            href={StorefrontRoutesMap.HOME}
                            className="flex items-center gap-2"
                        >
                            <div className="flex h-10 w-10 items-center justify-center">
                                <Image
                                    src="/logo.svg?height=40&width=40"
                                    alt="Логотип"
                                    width={32}
                                    height={32}
                                    className="h-full w-full"
                                />
                            </div>
                            <span className="hidden text-xl font-bold text-primary sm:inline">
                                {Env.NEXT_PUBLIC_WEBSITE_NAME}
                            </span>
                        </FastLink>

                        <nav className="hidden md:block">
                            <ul className="flex space-x-8">
                                {NavigationLinks.map(({ href, label }) => (
                                    <li key={label}>
                                        <FastLink
                                            href={href}
                                            className={cn(
                                                "text-gray-700 underline-offset-4 hover:text-primary",
                                                pathname === href &&
                                                    "text-primary underline",
                                            )}
                                        >
                                            {label}
                                        </FastLink>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div className="flex items-center justify-end gap-4">
                            {/* Mobile version - visible only on mobile */}
                            <a
                                href={`tel:${cleanPhoneNumber(Env.NEXT_PUBLIC_PHONE_NUMBER)}`}
                                className="flex items-center gap-2"
                            >
                                <PhoneContent
                                    phoneNumber={Env.NEXT_PUBLIC_PHONE_NUMBER}
                                />
                            </a>

                            <div className="flex">
                                <Button
                                    onClick={() => setDialogOpen(true)}
                                    className="w-full bg-primary hover:bg-primary/80"
                                >
                                    Заказать звонок
                                </Button>

                                <CallRequestDialog
                                    open={dialogOpen}
                                    onOpenChange={setDialogOpen}
                                />
                            </div>
                            <MobileMenu className="md:hidden" />
                        </div>
                    </div>
                </div>
            </header>
            <div className="h-[4rem] w-full" />
        </>
    )
}

const PhoneContent = ({ phoneNumber }: { phoneNumber: string }) => {
    return (
        <>
            <Phone className="h-5 w-5 text-primary" />
            <span className="hidden text-gray-700 hover:text-primary lg:inline">
                {phoneNumber}
            </span>
        </>
    )
}

const MobileMenu = ({ className }: { className?: string }) => {
    const [sheetOpen, setSheetOpen] = useState(false)

    const pathname = usePathname()
    useEffect(() => {
        setSheetOpen(false)
    }, [pathname])

    return (
        <div className={cn(className)}>
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetTrigger asChild>
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 bg-transparent backdrop-blur hover:bg-background/60"
                    >
                        <Menu className="h-6 w-6" />
                        <span className="sr-only">Открыть меню</span>
                    </Button>
                </SheetTrigger>
                <SheetTitle className="sr-only">Меню навигации</SheetTitle>
                <SheetContent side="right" className="w-[250px] sm:w-[300px]">
                    <nav className="mt-8 flex flex-col gap-4">
                        <FastLink
                            href="/"
                            className={`text-lg px-2 py-1 rounded-md hover:bg-gray-100 ${
                                pathname === "/"
                                    ? "font-bold text-primary underline underline-offset-4"
                                    : ""
                            }`}
                        >
                            Главная
                        </FastLink>
                        {NavigationLinks.map(({ href, label }) => (
                            <FastLink
                                key={label}
                                href={href}
                                className={`text-lg px-2 py-1 rounded-md hover:bg-gray-100 ${
                                    pathname === href
                                        ? "font-bold text-primary underline underline-offset-4"
                                        : ""
                                }`}
                            >
                                {label}
                            </FastLink>
                        ))}
                    </nav>
                </SheetContent>
            </Sheet>
        </div>
    )
}
